import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Briefcase, User } from 'lucide-react';
import { toast } from 'sonner';
import MemberAvatar from '@/components/MemberAvatar';

export default function PartnerTeamPage({ user }) {
  const { sponsorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ partner_name: '', partner_logo: '', members: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/partners/${sponsorId}/team`, { credentials: 'include' });
        if (!res.ok) throw new Error('Not found');
        setData(await res.json());
      } catch {
        toast.error('Failed to load team');
        navigate(`/partners/${sponsorId}`);
      }
      setLoading(false);
    })();
  }, [sponsorId, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="flex justify-center pt-32"><div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/partners/${sponsorId}`)} className="mb-4" data-testid="back-to-partner">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Partner
          </Button>

          <div className="flex items-center gap-4 mb-6">
            {data.partner_logo && <img src={data.partner_logo} alt={data.partner_name} className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-200 p-2" />}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Team at</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" data-testid="team-page-title">{data.partner_name}</h1>
              <p className="text-sm text-slate-500">{data.members.length} member{data.members.length === 1 ? '' : 's'}</p>
            </div>
          </div>

          {data.members.length === 0 ? (
            <Card className="p-12 text-center">
              <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No team members linked yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
              {data.members.map(m => (
                <Card key={m.user_id} onClick={() => navigate(`/members/${m.user_id}`)}
                  className="p-5 cursor-pointer hover:shadow-md hover:border-brand-navy/30 transition-all group" data-testid={`team-tile-${m.user_id}`}>
                  <div className="flex items-start gap-4">
                    {m.picture ? (
                      <MemberAvatar name={m.name} picture={m.picture} size={56} />
                    ) : (
                      <MemberAvatar name={m.name} size={56} />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-slate-800 group-hover:text-brand-navy transition-colors truncate">{m.name}</h3>
                      {m.profile?.role && (
                        <p className="text-sm text-slate-600 truncate flex items-center gap-1.5 mt-1">
                          <Briefcase className="h-3.5 w-3.5 text-slate-400" /> {m.profile.role}
                        </p>
                      )}
                      {(m.profile?.country || m.profile?.location) && (
                        <p className="text-xs text-slate-500 truncate flex items-center gap-1.5 mt-1">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" /> {[m.profile.location, m.profile.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {m.link_reason === 'manual' && (
                        <span className="inline-block mt-2 text-[10px] font-semibold text-brand-sage bg-brand-sage/10 px-2 py-0.5 rounded-full">Linked by admin</span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
